import { Alert, AlertDescription } from "@nimit9/signet-ui/components/alert"
import { Button } from "@nimit9/signet-ui/components/button"
import { Upload } from "lucide-react"
import { cn } from "@/lib/cn"

const SUPPORTED_BANKS = [
  { key: "hdfc", label: "HDFC Bank", logo: "/bank-logos/hdfc.png" },
  { key: "icici", label: "ICICI Bank", logo: "/bank-logos/icici.png" },
  { key: "sbi", label: "State Bank of India", logo: "/bank-logos/sbi.png" },
  { key: "amex", label: "American Express", logo: "/bank-logos/amex.png" },
  { key: "kotak", label: "Kotak Mahindra Bank", logo: "/bank-logos/kotak.png" },
]

/** A statement PDF nothing reads: "We can't read <bank> statements yet." `bank` = its display name, if known. */
export interface StepDropMiss {
  title: string
  body: string
  bank: string | null
}

interface StepDropProps {
  dragOver: boolean
  parseError: string | null
  miss?: StepDropMiss | null
  /** "Ask for my bank": idle, sending, sent or failed. */
  asked?: "idle" | "sending" | "sent" | "failed"
  onAskBank?: () => void
  onManualAdd?: () => void
  onDrop: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: () => void
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onCancel: () => void
}

export function StepDrop({
  dragOver,
  parseError,
  miss,
  asked = "idle",
  onAskBank,
  onManualAdd,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileChange,
  onCancel,
}: StepDropProps) {
  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <p className="font-mono text-[10.5px] uppercase tracking-widest text-accent-amber mb-1">
          Step 1 of 3
        </p>
        <h3 className="text-[20px] font-bold tracking-tight text-text-primary mb-1">
          Drop your statement
        </h3>
        <p className="text-[12.5px] text-text-muted mb-5 leading-relaxed">
          We figure out the bank automatically and read everything on this device.
        </p>

        {/* Drop zone */}
        <label
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={cn(
            "relative mb-4 flex cursor-pointer flex-col items-center gap-3 rounded-[14px] border-[1.5px] border-dashed px-5 py-9 text-center transition-all",
            dragOver
              ? "border-accent-amber bg-accent-amber/10"
              : "border-border-default bg-bg-elevated hover:border-accent-amber/50",
          )}
          style={
            !dragOver
              ? {
                  background:
                    "radial-gradient(ellipse at 50% 0%, rgba(245,158,11,0.07), transparent 55%), var(--bg-elevated)",
                }
              : undefined
          }
        >
          <div
            className="flex size-16 items-center justify-center rounded-2xl border"
            style={{
              background: "rgba(245,158,11,0.10)",
              borderColor: "rgba(245,158,11,0.22)",
            }}
          >
            <Upload className="size-7 text-accent-amber" strokeWidth={1.8} />
          </div>
          <div>
            <p className="text-[15px] font-semibold tracking-tight text-text-primary">
              Drop PDF, XLS or CSV here
            </p>
            <p className="mt-1 text-[11.5px] text-text-muted">
              Or{" "}
              <span className="text-accent-amber underline underline-offset-2">browse files</span>.
              Up to 50 MB.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-1.5 mt-1">
            {["PDF", "XLS / XLSX", "CSV"].map((fmt) => (
              <span
                key={fmt}
                className="rounded-full border border-border-default bg-bg-elevated px-2.5 py-0.5 font-mono text-[9.5px] tracking-wide text-text-muted"
              >
                {fmt}
              </span>
            ))}
          </div>
          <input
            type="file"
            accept=".pdf,.xls,.xlsx,.csv"
            className="hidden"
            onChange={onFileChange}
          />
        </label>

        {/* Supported banks */}
        <div className="mb-3 flex items-center gap-2 rounded-[10px] border border-border-subtle bg-bg-elevated px-4 py-2.5">
          <span className="text-[11px] text-text-muted">Works with</span>
          {SUPPORTED_BANKS.map(({ key, label, logo }) => (
            <img
              key={key}
              src={logo}
              alt={label}
              title={label}
              className="size-4 rounded-[3px] object-contain"
            />
          ))}
          <span className="ml-auto text-[10.5px] text-text-muted">+ 12 more</span>
        </div>

        {/* Privacy strip */}
        <div
          className="flex items-center gap-2 rounded-[10px] border px-3.5 py-2.5 text-[11.5px]"
          style={{
            background: "rgba(52,211,153,0.06)",
            borderColor: "rgba(52,211,153,0.18)",
            color: "var(--accent-green)",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <title>Privacy Icon</title>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Nothing leaves your device · read on-device, encrypted at rest
        </div>

        {miss ? (
          <div className="mt-4 space-y-2" data-testid="import-miss">
            <h4 className="text-title font-semibold text-text-primary">{miss.title}</h4>
            <p className="text-body text-text-muted">{miss.body}</p>
            <p className="text-caption text-text-muted">
              Nothing was uploaded. For other files, use Manual CSV Import.
            </p>
            <div className="flex flex-col items-start gap-1 pt-1">
              {!miss.bank ? null : asked === "sent" ? (
                <p className="text-body text-text-muted">Sent. {miss.bank} is on the list.</p>
              ) : (
                <>
                  {asked === "failed" && (
                    <p className="text-body text-text-muted">
                      Couldn't send. Check the connection and try again.
                    </p>
                  )}
                  <Button
                    data-testid="import-ask-bank"
                    disabled={asked === "sending"}
                    onClick={onAskBank}
                  >
                    Ask for my bank
                  </Button>
                </>
              )}
              {onManualAdd && (
                <Button variant="link" data-testid="import-manual" onClick={onManualAdd}>
                  Add transactions by hand
                </Button>
              )}
            </div>
          </div>
        ) : parseError ? (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription className="text-[12.5px]">{parseError}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3.5">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled>
          Continue
        </Button>
      </div>
    </>
  )
}
